import type {
    MoneyMapNewApexAccount,
    MoneyMapNewFruitfulAccount,
} from '@fruitful/types';
import type { Href } from 'expo-router/build/typed-routes/types';

export enum GoWorkflowStepType {
    MONEY_MAP = 'MONEY_MAP',
    CASH_ACCOUNTS = 'CASH_ACCOUNTS',
    CARD_ACCOUNTS = 'CARD_ACCOUNTS',
    APEX_ACCOUNTS = 'APEX_ACCOUNTS',
    CMM_IM = 'CMM_IM',
    INCOME_SPLIT = 'INCOME_SPLIT',
}

export interface GoWorkflowStep {
    index: number;
    type: GoWorkflowStepType;
    url: Href;
    completeness:
        | ReviewMoneyMapStepCompleteness
        | CashAccountsStepCompleteness
        | CardAccountsStepCompleteness
        | ApexAccountsStepCompleteness
        | ImTeaserStepCompleteness
        | IncomeSplitStepCompleteness;
}

interface MoneyMapWorkflowStepCompleteness {
    /**
     * Whether this step is included in the workflow for the given Money Map.
     * If the step is not relevant for the given Money Map, it is excluded
     * (e.g. no accounts to create, or no income split)
     */
    isIncluded: boolean;

    /**
     * Whether this step can be visited yet (e.g. Income Split step cannot
     * be visited until all proposed accounts have been created)
     */
    isEnabled: boolean;

    /**
     * Whether the current member has completed this step (e.g. created their accounts)
     */
    isCompleteForMember: boolean;

    /**
     * Whether this step is entirely complete (i.e. complete for both partner members)
     */
    isCompleteForMembership: boolean;
}

export interface ReviewMoneyMapStepCompleteness extends MoneyMapWorkflowStepCompleteness {
    /**
     * TODO MoneyMapReviewState - This does not have a supporting data model, which is deferred scope to come back to.
     * For now, define based on !AccountsStep.memberHasProposedAccounts
     */
    memberHasReviewed: boolean;

    /**
     * TODO MoneyMapReviewState - This does not have a supporting data model, which is deferred scope to come back to.
     * For now, define based on !AccountsStep.partnerHasProposedAccounts
     * Should be `undefined` for solo membership
     */
    partnerHasReviewed?: boolean;
}

export interface CashAccountsStepCompleteness extends MoneyMapWorkflowStepCompleteness {
    jointAccountsToOpen: MoneyMapNewFruitfulAccount[];
    memberSoloAccountsToOpen: MoneyMapNewFruitfulAccount[];
    memberJointAccountsToAccept: MoneyMapNewFruitfulAccount[];
    partnerSoloAccountsToOpen: MoneyMapNewFruitfulAccount[];
    partnerJointAccountsToAccept: MoneyMapNewFruitfulAccount[];
}

export interface CardAccountsStepCompleteness extends MoneyMapWorkflowStepCompleteness {
    jointAccountsToOpen: MoneyMapNewFruitfulAccount[];
    memberSoloAccountsToOpen: MoneyMapNewFruitfulAccount[];
    memberJointAccountsToAccept: MoneyMapNewFruitfulAccount[];
    partnerSoloAccountsToOpen: MoneyMapNewFruitfulAccount[];
    partnerJointAccountsToAccept: MoneyMapNewFruitfulAccount[];
}

export interface ApexAccountsStepCompleteness extends MoneyMapWorkflowStepCompleteness {
    /* True when every APEX account in this step has derived.isInGoodStanding. */
    areAllAccountsInGoodStanding: boolean;
    /**
     * TRUE  when one or more accounts is created, but pending IDV/Watchlist
     * FALSE when no accounts are pending investigation run
     */
    isCompletionPendingInvestigationRun: boolean;
    jointAccountsToOpen: MoneyMapNewApexAccount[];
    memberSoloAccountsToOpen: MoneyMapNewApexAccount[];
    memberJointAccountsToAccept: MoneyMapNewApexAccount[];
    partnerSoloAccountsToOpen: MoneyMapNewApexAccount[];
    partnerJointAccountsToAccept: MoneyMapNewApexAccount[];
}

/**
 * A pseudo-completeness step for CMM money maps that aren't allowed to have IM
 * accounts.  We want to show the member(s) a screen that tells them that their
 * guide will help them open their IM accounts.  This step isn't shown in the
 * list of MM steps or returned by the server as a completeness step.  It exists
 * entirely client-side.
 */
export interface ImTeaserStepCompleteness extends MoneyMapWorkflowStepCompleteness {}

export interface IncomeSplitStepCompleteness extends MoneyMapWorkflowStepCompleteness {
    incomeSplitCreated: boolean;
}

export interface MoneyMapCompleteness {
    reviewMoneyMapStep: ReviewMoneyMapStepCompleteness;
    cashAccountsStep: CashAccountsStepCompleteness;
    cardAccountsStep: CardAccountsStepCompleteness;
    apexAccountsStep: ApexAccountsStepCompleteness | undefined;
    incomeSplitStep: IncomeSplitStepCompleteness;
}
